import React from "react";
import { TransformIcon } from "vega-icons";

import { faro } from "@grafana/faro-react";

import { useOverflowObserver } from "../../hooks/useOverflowObserver";
import { useScreenshot } from "../../hooks/useScreenshot";
import { m } from "../../paraglide/messages";
import { cnTwMerge } from "../../utils/cnTwMerge";
import { ThemedIconButton } from "../Buttons/ThemedIconButton";
import { Card } from "../shadcn/card";
import { useToast } from "../Toast/useToast";
import { Tooltip } from "../Tooltip/Tooltip";
import { TooltipInfoIcon } from "../Tooltip/TooltipInfoIcon";

import { AnalyticsCardError } from "./AnalyticCardError";
import { fixResponsiveContainerDimensions } from "./fixResponsiveContainerDimensions";

export type AnalyticCardTitleVariant = "large" | "small";

const variantStyles: Record<AnalyticCardTitleVariant, string> = {
  large: "body-large-semibold",
  small: "body-base-medium",
};

export type AnalyticCardVariant = "compact" | "default";

type AnalyticCardVariantConfig = {
  fullHeight: boolean;
  titleJustifyBetween: boolean;
  titleVariant: AnalyticCardTitleVariant;
};

const variantConfigs: Record<AnalyticCardVariant, AnalyticCardVariantConfig> = {
  default: {
    fullHeight: false,
    titleJustifyBetween: false,
    titleVariant: "large",
  },
  compact: {
    fullHeight: true,
    titleJustifyBetween: true,
    titleVariant: "small",
  },
};

export const AnalyticCard = ({
  title,
  tooltip,
  variant = "default",
  titleVariant,
  actions,
  children,
  canScreenShot = false,
  isLoading = false,
  isError = false,
  onRefresh,
  fullHeight,
  fixedWidth = false,
  titleJustifyBetween,
  disabled = false,
  isSelected = false,
  dataTestId,
}: {
  title: string;
  tooltip?: string;
  variant?: AnalyticCardVariant;
  titleVariant?: AnalyticCardTitleVariant;
  actions?: React.ReactNode;
  children: React.ReactNode;
  canScreenShot?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  fullHeight?: boolean;
  fixedWidth?: boolean;
  titleJustifyBetween?: boolean;
  disabled?: boolean;
  isSelected?: boolean;
  dataTestId: string;
}) => {
  const { captureAndDownload, ref } = useScreenshot({
    preprocessElement: fixResponsiveContainerDimensions,
  });

  const variantConfig = variantConfigs[variant];

  const resolvedFullHeight = fullHeight ?? variantConfig.fullHeight;
  const resolvedTitleJustifyBetween =
    titleJustifyBetween ?? variantConfig.titleJustifyBetween;
  const resolvedTitleVariant = titleVariant ?? variantConfig.titleVariant;

  const showToast = useToast();

  const [titleRef, isTitleOverflowing] = useOverflowObserver<HTMLDivElement>({
    axis: "horizontal",
    includeDescendants: true,
  });

  const handleScreenshot = async () => {
    if (disabled) {
      showToast({
        variant: "destructive",
        title: m.analytics_screenshotUnavailable_title(),
        description: m.analytics_screenshotUnavailable_message(),
      });

      return;
    }

    try {
      await captureAndDownload(`${title}-screenshot`);
    } catch (error) {
      showToast({
        variant: "destructive",
        title: m.analytics_screenshotFailed_title(),
      });
      faro.api.pushError(error as Error, {
        type: "screenshot",
        context: {
          component: "AnalyticCard",
          chartTitle: title,
          timestamp: new Date().toISOString(),
        },
      });
    }
  };

  if (isError) {
    return <AnalyticsCardError dataTestId={dataTestId} onRefresh={onRefresh} />;
  }

  return (
    <Card
      className={cnTwMerge(
        "flex flex-col p-6 shadow-none",
        fixedWidth ? "min-w-60" : "w-full",
        resolvedFullHeight && "h-full",
        isSelected && "border-border/brand bg-background/brand/opacity",
      )}
      data-testid={dataTestId}
      isLoading={isLoading}
      ref={canScreenShot ? ref : undefined}
    >
      <div className="flex flex-col gap-2 w-full min-w-0">
        <div className="flex w-full items-center justify-between gap-4 min-w-0">
          <div
            className={cnTwMerge(
              "flex items-center min-w-0 flex-1",
              resolvedTitleJustifyBetween ? "justify-between" : "gap-2",
            )}
            ref={titleRef}
          >
            {isTitleOverflowing ? (
              <Tooltip content={title}>
                <p
                  className={cnTwMerge(
                    variantStyles[resolvedTitleVariant],
                    "truncate cursor-pointer",
                  )}
                  data-testid={`${dataTestId}-title`}
                >
                  {title}
                </p>
              </Tooltip>
            ) : (
              <p
                className={cnTwMerge(
                  variantStyles[resolvedTitleVariant],
                  "truncate",
                )}
                data-testid={`${dataTestId}-title`}
              >
                {title}
              </p>
            )}
            {tooltip ? <TooltipInfoIcon content={tooltip} /> : null}
          </div>
          {actions || canScreenShot ? (
            <div className="flex items-center gap-4 flex-shrink-0">
              {actions}
              {canScreenShot ? (
                <ThemedIconButton
                  data-exclude-from-screenshot
                  dataTestId={`${dataTestId}-screenshot`}
                  icon={<TransformIcon />}
                  onClick={handleScreenshot}
                  size="small"
                  variant="noBackground"
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {children}
    </Card>
  );
};
